import { Alert, Autocomplete, Button, Checkbox, FormControl, FormControlLabel, FormGroup, Grid, TextField } from '@mui/material';
import { FormEvent, SyntheticEvent, useContext, useState } from 'react';
import { Store } from '../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import { filterStoreOptions } from '../../../utils';
import { StoreScope } from '../components/StoreScopePicker';
import QuickDateFilters, { DateRange } from '../components/QuickDateFilters';
import AdvancedFilters from '../components/AdvancedFilters';

type IProps = {
  /** Fired after a report is successfully queued so the page can refresh status panels. */
  onGenerated?: () => void;
  onGenerationFailed?: (message?: string) => void;
  storeScope?: StoreScope;
  /** Full scope pickers rendered inside the merged Advanced filters collapse. */
  advancedScopeContent?: React.ReactNode;
}

const OutstandingDeliveriesReportForm = (props: IProps) => {
  const { user, selectedStore } = useContext(UserContext);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  // Flipped on failed submit validation so required fields render their error state.
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState<boolean>(false);
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false);
  const { runOutstandingDeliveriesReport } = useNswagClient();
  const { t } = useTranslation('common');
  const { t: datepickerLabels } = useTranslation('orderHistory');

  const currentFranchiseStores = user?.stores?.filter(x => x.franchiseId === selectedStore?.franchiseId) ?? [];

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectedStores([]);
    setCheckedAllStores(checked);
  };

  const handleSelectChange = (event: SyntheticEvent, value: Store[]) => {
    setSelectedStores(value);
  };

  const handleQuickDateRange = (range: DateRange) => {
    setFromDate(range.from);
    setToDate(range.to);
  };

  const getMinDate = () => {
    return new Date('1900-01-01').toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    // Both range dates are required — submitting without them sends a bad request.
    if (!fromDate || !toDate) {
      setShowErrors(true);
      setErrorMessage(t('reportingV2.date-range-required'));
      return;
    }
    setIsGenerating(true);
    setErrorMessage('');

    const storeNumbers = checkedAllStores
      ? currentFranchiseStores.map(x => x.storeNumber as string)
      : selectedStores.map(x => x.storeNumber as string);

    const selectedAllStores = checkedAllStores || storeNumbers.length === currentFranchiseStores.length;

    // When the shared scope picker drives selection, defer store scoping to the backend:
    // an empty store list means "all stores of the selected franchise".
    const scopedByPicker = !!props.storeScope;

    const request = {
      from: fromDate,
      to: toDate,
      stores: scopedByPicker
        ? (props.storeScope!.storeNumbers.length > 0 ? props.storeScope!.storeNumbers : [])
        : (selectedAllStores ? [] : storeNumbers),
      reportOnAllAvailableStores: scopedByPicker ? props.storeScope!.storeNumbers.length === 0 : selectedAllStores,
      tenantId: props.storeScope?.tenantId ?? undefined,
      franchiseId: props.storeScope?.franchiseId ?? undefined,
    };

    runOutstandingDeliveriesReport(request).then(() => {
      setIsRequestSubmitted(true);
      props.onGenerated?.();
    }).catch((error) => {
      props.onGenerationFailed?.(typeof (error) === 'string' ? error : undefined);
      console.log(error);
    }).finally(() => {
      setIsGenerating(false);
      setIsRequestSubmitted(false);
    });
  };

  return (
    <form onSubmit={handleSubmit}
      noValidate>
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        {!props.storeScope && (
          <>
            <Grid item
              sm={6}
              xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  multiple
                  limitTags={2}
                  options={currentFranchiseStores}
                  value={selectedStores}
                  onChange={handleSelectChange}
                  disabled={checkedAllStores}
                  getOptionKey={(option) => option.storeNumber ?? ''}
                  getOptionLabel={(option) => option.storeName ?? ''}
                  filterOptions={filterStoreOptions}
                  renderInput={(params) => (
                    <TextField {...params}
                      label={t('reporting.select-stores')}
                      placeholder={t('reporting.select-stores')} />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item
              sm={6}
              xs={12}>
              <FormGroup style={{ textAlign: 'right' }}>
                <FormControlLabel
                  control={<Checkbox
                    checked={checkedAllStores}
                    onChange={handleSelectAllChange} />}
                  label={t('reporting.all-stores')}
                />
              </FormGroup>
            </Grid>
          </>
        )}
        <Grid item
          xs={12}>
          <QuickDateFilters onChange={handleQuickDateRange} />
        </Grid>
        <Grid item
          xs={12}>
          <AdvancedFilters>
            <Grid container
              spacing={10}>
              {props.advancedScopeContent && (
                <Grid item
                  xs={12}>
                  {props.advancedScopeContent}
                </Grid>
              )}
              <Grid item
                sm={6}
                xs={12}>
                <DatePickerLocalized
                  label={t('reporting.start-date')}
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue ?? '')}
                  error={showErrors && !fromDate}
                  useCalendar={false}
                  shouldDisableDate={(date: string) =>
                    date < getMinDate() || date > (toDate ? toDate : getMaxDate())
                  }
                  translations={{
                    clearButtonLabel: datepickerLabels('calendar.clearButtonLabel'),
                    todayButtonLabel: datepickerLabels('calendar.todayButtonLabel'),
                    cancelButtonLabel: datepickerLabels('calendar.cancelButtonLabel'),
                    okButtonLabel: datepickerLabels('calendar.okButtonLabel'),
                    datePickerToolbarTitle: t('reporting.start-date'),
                  }}
                />
              </Grid>
              <Grid item
                sm={6}
                xs={12}>
                <DatePickerLocalized
                  label={t('reporting.end-date')}
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue ?? '')}
                  error={showErrors && !toDate}
                  useCalendar={false}
                  shouldDisableDate={(date: string) =>
                    date < (fromDate ? fromDate : getMinDate()) || date > getMaxDate()
                  }
                  translations={{
                    clearButtonLabel: datepickerLabels('calendar.clearButtonLabel'),
                    todayButtonLabel: datepickerLabels('calendar.todayButtonLabel'),
                    cancelButtonLabel: datepickerLabels('calendar.cancelButtonLabel'),
                    okButtonLabel: datepickerLabels('calendar.okButtonLabel'),
                    datePickerToolbarTitle: t('reporting.end-date'),
                  }}
                />
              </Grid>
            </Grid>
          </AdvancedFilters>
        </Grid>
        {
          errorMessage &&
          <Grid item
            xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        }
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            disabled={
              isGenerating ||
              isRequestSubmitted ||
              (props.storeScope !== undefined && props.storeScope.franchiseId == null)
            }
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default OutstandingDeliveriesReportForm;
